import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { Text } from '@chakra-ui/react'
import { EDITOR_TOOLS } from '@editor/config/constants'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { InspectionItem, InspectionItemType } from '@editor/interfaces/inspection'
import { PlaneSide } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { useInspectionItems } from '@editor/pages/projects/hooks/useInspectionItems'
import { DepthEstimationResultKey, estimateDepth, getDepthEstimationItems } from '@editor/services/DepthEstimation'
import { ERROR_PROCESS, getErrorForToast } from '@editor/services/ErrorHandler'
import { shapeIdArrayToShapeIds } from '@editor/services/Util'
import { getVolumeEstimationItems, pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { RootState } from '@editor/store/app'
import { addWorkingTool, removeWorkingTool, setSelectedElementIds, toggleHiddenElementIds } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'
import * as Sentry from '@sentry/react'
import { cloneDeep } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { useDispatch, useSelector } from 'react-redux'

import { INITIAL_DEPTH_ESTIMATION_TYPES, setDepthEstimationTypes } from '../store'

/**
 * Initialize the editor for the depth estimation tool.
 * Note that when a shape is selected, there is an additional mechanic to filter/add more shapes to the selections.
 * This mechanics is located in MainCanvas::changeSelectedShapeIds and addAdditionalSelectedShapeIds.
 *
 * @param props The editor context.
 */
export default function useEditor({
  selectedTool,
  collidingShapeIds,
  prevSelectedTool,
  changeTool,
  changeIsJobRunning,
  changeCollidingShapeIds,
}: Editor): EditorConfig {
  // Toast
  const toastIdRef = useRef<string>(undefined)
  const successToastIdRef = useRef<string>(undefined)
  const infoToastIdRef = useRef<string>(undefined)

  // Hooks
  const { fetchInspectionItems } = useInspectionItems()

  // Context
  const { showErrorModal } = useContext(GlobalModalContext)
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const inspectionSheet = useSelector((state: RootState) => state.inspectionItems.inspectionSheet)
  const inspectionItems = useSelector((state: RootState) => state.inspectionItems.items)
  const shapes = useSelector((state: RootState) => state.shapes.shapes)
  const selectedElementIds = useSelector((state: RootState) => state.toolUtil.core.selectedElementIds)

  // State
  const [isLoading, setIsLoading] = useState(false)

  // Vars
  const isSelectedTool = selectedTool === EDITOR_TOOLS.DEPTH
  const shapesToSubmit = shapeIdArrayToShapeIds(shapes, selectedElementIds)
  const isEnoughShapesSelected = shapesToSubmit.cylinders.length > 0 && shapesToSubmit.polygons.length > 0
  const isSaveable = isSelectedTool && !isLoading && isEnoughShapesSelected

  // ------------ Estimate depth ------------ //
  const handleSave = useCallback(async () => {
    if (!project || !inspectionArea || !inspectionSheet) {
      return false
    }

    const volumeItem = getVolumeEstimationItems(inspectionItems).find((item) =>
      [...(item.shape_ids.polygons || [])].some((id) => selectedElementIds.includes(id)),
    )
    if (!volumeItem?.inspection_item_id) {
      return false
    }

    const token = await getAccessToken()
    if (!token) {
      changeIsJobRunning(false)
      return false
    }

    // Change page state to loading
    setIsLoading(true)
    changeIsJobRunning(true)
    dispatch(addWorkingTool(EDITOR_TOOLS.DEPTH))

    // clear colliding shape IDs for shapes used before running
    changeCollidingShapeIds(collidingShapeIds.filter((id) => !selectedElementIds.includes(id)))

    // Call API to get job token
    const jobToken = await estimateDepth(
      token,
      project.project_id,
      inspectionArea.inspection_area_id,
      inspectionSheet.inspection_sheet_id,
      volumeItem.inspection_item_id,
      shapesToSubmit,
      showErrorModal,
    )

    if (!jobToken) {
      // Reset page state
      setIsLoading(false)
      changeIsJobRunning(false)
      dispatch(removeWorkingTool(EDITOR_TOOLS.DEPTH))
      return false
    }

    // Poll for job completion
    let success: Record<DepthEstimationResultKey, InspectionItem> | null = null
    try {
      success = await pollAsyncJobStatus<Record<DepthEstimationResultKey, InspectionItem>>(token, jobToken)
    } catch (error) {
      Sentry.captureException(error)
      showErrorModal('かぶり厚測定中にエラーが発生しました。時間をおいて再度お試しください。')
      success = null
    }

    mixpanel.track('Estimate depth', {
      Tool: selectedTool, // a bit redundant, but we want to be consistent
      'Volume item': volumeItem.inspection_item_id,
      'Shape IDs': selectedElementIds,
      'Shape amount': selectedElementIds.length,
      Success: !!success,
    })

    let newCollidingShapeIds: string[] = []
    if (success) {
      // re-show all planes of the volume
      if (!success.plane_to_plane_distance.status_message) {
        dispatch(toggleHiddenElementIds({ ids: volumeItem.shape_ids.polygons, invisible: false }))
      }

      await fetchInspectionItems()

      // Set the depth items as selected items. Make sure to only set PlaneToCylindersDistance items here.
      dispatch(
        setSelectedElementIds([
          // the selection are individual to min/max/avg so we need to add those
          `${success.plane_to_cylinders_distance1.inspection_item_id}-min`,
          `${success.plane_to_cylinders_distance1.inspection_item_id}-max`,
          `${success.plane_to_cylinders_distance1.inspection_item_id}-avg`,
          `${success.plane_to_cylinders_distance2.inspection_item_id}-min`,
          `${success.plane_to_cylinders_distance2.inspection_item_id}-max`,
          `${success.plane_to_cylinders_distance2.inspection_item_id}-avg`,
        ]),
      )

      const statusIds = new Set<number>()
      ;(Object.keys(success) as DepthEstimationResultKey[]).forEach((row) => {
        // This specific type has encountered an error, get its status ID
        if (success[row].status_message) {
          const statusId = (JSON.parse(success[row].status_message) as { status_id?: number } | undefined)?.status_id
          if (statusId) {
            statusIds.add(statusId)

            const { colliding_shape_ids } = success[row]
            if (colliding_shape_ids) {
              newCollidingShapeIds = [...newCollidingShapeIds, ...colliding_shape_ids]
            }
          }
        }
      })
      changeCollidingShapeIds(newCollidingShapeIds)

      if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
      successToastIdRef.current = toaster.create({
        title: 'かぶり厚測定が完了しました',
        type: 'success',
        duration: 3000,
        closable: true,
      })

      // Show toast for every unqiue status ID
      if (statusIds.size) {
        statusIds.forEach((statusId) => {
          toastIdRef.current = toaster.create({
            ...getErrorForToast(ERROR_PROCESS.ESTIMATE_DEPTH, statusId),
            type: 'warning',
            duration: 10000,
            closable: true,
          })
        })
      }
    }

    // Reset page state
    setIsLoading(false)
    changeIsJobRunning(false)
    dispatch(removeWorkingTool(EDITOR_TOOLS.DEPTH))

    return true
  }, [
    selectedTool,
    selectedElementIds,
    project,
    inspectionArea,
    inspectionSheet,
    inspectionItems,
    collidingShapeIds,
    shapesToSubmit,
    showErrorModal,
    dispatch,
    getAccessToken,
    fetchInspectionItems,
    changeIsJobRunning,
    changeCollidingShapeIds,
  ])

  /**
   * Set depth estimate data after loading inspection items
   */
  useEffect(() => {
    const items = getDepthEstimationItems(inspectionItems)
    const depthTypes = cloneDeep(INITIAL_DEPTH_ESTIMATION_TYPES)

    // Old items have no plane_side so treat the first plane to cylinder as Type 1
    depthTypes[0].inspectionItems = items
      // The reduce is to make sure we only get 1 plane to cylinder distance per volume
      .reduce<InspectionItem[]>((arr, item) => {
        if (item.plane_side) {
          return arr
        }
        if (arr.some((it) => it.volume_id === item.volume_id)) {
          return arr
        }
        if (
          item.item_type === InspectionItemType.PlaneToCylindersDistance &&
          !!item?.plane_to_cylinders_distance?.estimated_value
        ) {
          return [...arr, item]
        }

        return arr
      }, [])

    // If there's another plane to cylinder different from found on Type 1, then that shall be Type 2
    depthTypes[1].inspectionItems = items.filter(
      (item) =>
        !item.plane_side &&
        item.item_type === InspectionItemType.PlaneToCylindersDistance &&
        !depthTypes[0].inspectionItems?.some((it) => it.inspection_item_id === item.inspection_item_id) &&
        !!item?.plane_to_cylinders_distance?.estimated_value,
    )

    // Top side
    depthTypes[2].inspectionItems = items.filter((item) => item.plane_side === PlaneSide.UPPER)

    // Bottom side
    depthTypes[3].inspectionItems = items.filter((item) => item.plane_side === PlaneSide.LOWER)

    dispatch(setDepthEstimationTypes(depthTypes))
  }, [inspectionItems, shapes, dispatch])

  /**
   * Toggle info panels when tool is selected
   */
  useEffect(() => {
    if (!isSelectedTool) return

    dispatch(
      setAttentionText({
        message:
          '平面を選択すると自動で領域内の鉄筋が選択されます。' +
          '鉄筋は手動で除外/追加することもできます。\n' +
          '選択が完了したら右下の測定ボタンをクリックしてください。',
      }),
    )
  }, [isSelectedTool, prevSelectedTool, dispatch])

  /**
   * Handle apply operation.
   */
  const handleApply = useCallback(async () => {
    dispatch(setAttentionText({ message: 'かぶり厚測定中...' }))
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }, [dispatch, handleSave])

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = useCallback(() => {
    dispatch(setAttentionText({ message: '' }))

    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, true)

    if (isSaveable) {
      if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
      infoToastIdRef.current = toaster.create({
        title: 'かぶり厚測定を開始しました',
        description:
          'かぶり厚測定が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }, [changeTool, handleSave, dispatch, isSaveable])

  return {
    buttons: {
      apply: {
        key: 'depth-estimate-apply',
        label: `かぶり厚を測定`,
        loadingLabel: '測定中',
        tooltip: !isEnoughShapesSelected ? (
          <Text color="orange.400">平面の領域と１つ以上の鉄筋を選択してください</Text>
        ) : undefined,
        onClick: handleApply,
        isShown: () => isSelectedTool,
        isLoading: () => isLoading,
        isDisabled: () => !isSaveable,
      },
      submit: {
        key: 'depth-estimate-submit',
        label: isSaveable ? '測定して終了' : undefined,
        loadingLabel: '測定中',
        isLoading: () => false,
        isShown: () => isSelectedTool,
        isDisabled: () => false,
        onClick: handleSubmit,
      },
    },
  }
}
